
import Page from '@root/common/base/Page';
import ListSkeletonLoader from '@root/common/components/base/loader/ListSkeletonLoader.vue';
import { GetAuthorWithHeadlines } from '@root/modules/author/types/getAuthorWithHeadlines';
import { HeadlineSettings } from '@root/modules/headlines/types/headlines';
import AuthorWithHeadlinesService from '@root/modules/author/services/AuthorWithHeadlines.service';
import Author from '@root/modules/author/components/Author.vue';
import AuthorBreadcrumbs from '@root/modules/author/components/AuthorBreadcrumbs.vue';
import HeadlineList from '@root/common/components/base/headline/HeadlineList.vue';
import { getPageOffset } from '@root/common/utils/getPageOffset';
import { BlockCollection } from '@headlines/components/block';
import { getEnv } from '@root/common/utils/getEnv';
import getAliasByLocale from '@root/common/utils/getAliasByLocale';
import { stripTags } from '@root/common/filters/htmlFormater';

interface Data {
  author: GetAuthorWithHeadlines['data']['authors']['data'][0] | null;
  headlines: GetAuthorWithHeadlines['data']['authors']['data'][0]['articles'];
  headlineSettings: HeadlineSettings;
  titleBlock: Record<string, any>;
}

type Person = {
  '@type': string;
  '@id': string;
  name: string;
  image?: Record<string, any>;
  description?: string;
  memberOf?: Record<string, any>[];
};

type ProfilePageJsonLd = {
  '@context': string;
  '@type': string;
  mainEntity: Record<string, any>;
  url: string;
  name: string;
  description?: string;
  primaryImageOfPage?: Record<string, any>;
};

export default Page.extend<Data, unknown, unknown, unknown>({
  components: {
    Author,
    AuthorBreadcrumbs,
    HeadlineList,
    ListSkeletonLoader,
    BlockType8: BlockCollection.BlockType8,
  },
  data() {
    return {
      author: null,
      headlines: {
        items: [],
        pager: {
          offset: 0,
          count: 0,
        },
      },
      headlineSettings: {
        author: false,
        publishDate: true,
        titleComment: false,
        type: 'grid',
      },
      titleBlock: {
        data: {
          title: {
            text: 'Autoriaus naujienos',
          },
        },
        settings: {
          text: true,
          colorBlock: '#3858f5',
        },
      },
    };
  },
  async fetch() {
    const limit = 20;
    const offset = getPageOffset(limit, Number(this.$route.query.page));
    const slug = this.$route.params.slug;

    if (slug) {
      try {
        const authorWithHeadlinesService = new AuthorWithHeadlinesService({ req: this.$ssrContext?.req });
        const data = await authorWithHeadlinesService.fetch({ slug, offset, limit });

        if (!data) {
          authorWithHeadlinesService.handleError({ message: 'author.error.not_found', statusCode: 404 });
          return;
        }

        this.author = data;

        if (data.articles.items && data.articles.items.length) {
          this.headlines.items = data.articles.items.filter(
            (item) => item.primaryCategory.channel.url !== 'www.delfi.lt/elta' && item.primaryCategory.channel.url !== 'www.delfi.lt/fone'
          );
        }

        if (data.articles.pager) {
          this.headlines.pager = data.articles.pager;
        }
      } catch (e) {
        this.handlePageError(e);
      }
    }
  },
  methods: {
    changePage() {
      this.$fetch();
    },
  },
  mounted() {
    this.restoreScroll();
  },
  head() {
    const meta = this.getPageMeta();

    if (this.author) {
      const { name } = this.author;
      const isEmployee = this.author.employeeId;
      const query = this.$nuxt.context.query;
      const pageTitle = query?.page ? `${name}, ${this.$t('words.page')} ${query.page}` : name;

      meta.setTitle({ title: pageTitle });

      const description = this.author?.attributes?.find((item) => item.key === 'description')?.value || null;
      if (description) {
        meta.description = meta.ogDescription = stripTags(description);
      }

      const picture = this.author.image;
      let pictureObject = null;
      if (typeof picture !== 'undefined') {
        const picture = this.author.image;
        const focuspoint = picture?.attributes?.cropperData?.focuspoint;
        const focusPointString = focuspoint?.x && focuspoint?.y ? `&fx=${focuspoint?.x}&fy=${focuspoint?.y}` : ``;
        const url = getEnv('mediaApiCropper');
        const pictureUrl = `${url}/${picture?.id}.jpg?w=800&h=1200${focusPointString}`;
        meta.imageData = { src: pictureUrl, width: 800, height: 1200 };
        pictureObject = {
          '@type': 'ImageObject',
          url: `${pictureUrl}/${picture?.id}.jpg?w=800&h=1200${focusPointString}`,
          width: 800,
          height: 1200,
        };
      }

      const { locale } = this.$channelConfig('settings');
      const { base } = this.$channelConfig('meta');
      const path = getAliasByLocale(locale, 'authors');

      // Generate Person entity data
      const person: Person = {
        '@type': 'Person',
        '@id': `${base.baseUrl}/${path}/${this.author.slug}/#author`,
        name: this.author.name,
      };

      if (pictureObject) {
        person.image = pictureObject;
      }

      if (description) {
        person.description = stripTags(description);
      }

      if (isEmployee) {
        person.memberOf = [
          {
            '@type': 'Organization',
            name: base.title,
            logo: {
              '@type': 'ImageObject',
              url: base.imageData.src,
            },
          },
        ];
      }

      // Generate ProfilePage jsonLd
      const profilePageJsonLd: ProfilePageJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          ...{ '@type': 'Person' },
          ...person,
        },
        url: meta.url,
        name: this.author.name,
      };

      if (description) {
        profilePageJsonLd.description = stripTags(description);
      }

      if (pictureObject) {
        profilePageJsonLd.primaryImageOfPage = pictureObject;
      }

      meta.script = [{ hid: 'profile-page-ld-json', type: 'application/ld+json', json: profilePageJsonLd }];
    }

    return {
      title: meta.title,
      meta: meta.data,
      link: meta.link,
      script: meta.script,
    };
  },
});
